// AUDIO THAT STARTS BY ITSELF, ON A PHONE.
//
// Khari, 2026-10-28: *"while on mobile browser, the music from the plaza does
// not play. the radio fades out, but the plaza remains quiet."*
//
// iOS Safari lets an <audio> element begin only if its FIRST play() happens
// inside a tap. After that the same element may play from anywhere — which is
// how the station works (audioEngine.unlock blesses its one element on Enter).
// The plaza stage made a new Audio() for every song from the frame loop, long
// after any tap: iOS refused each play(), the catch swallowed it, and there was
// silence with no error. (The radio still faded: holding it quiet just pauses
// its own element.)
//
// So everything in the city that starts by itself — the stage's songs and the
// DJ's heads-up, the street's voices, a story's score — BORROWS an element from
// here instead of making one: blessed on a tap by a silent play/pause, given
// its real song when borrowed (the blessing survives a new src), handed back
// when done. Every tap tops the pool back up. Desktop browsers never needed any
// of this; they get the same elements and behave exactly as before.
//
// Listeners a borrower adds through `onAudio` are removed when the element is
// handed back, so one borrower's 'ended' can never fire for the next.

import { SILENT_WAV } from './silentClip';
// The stage holds three at once (this song, the next one loading, the DJ); the
// street a few voices and a score.
// 16, not 8 (2026-10-29): at the plaza the street's voices, the street music and
// the stage borrow at once, and an empty pool hands out a plain element iOS will
// not play — the concert stayed silent on an iPhone.
const SIZE = 16;

const free = [];                 // blessed and idle
const blessed = new WeakSet();   // every element a tap has blessed
let blessing = 0;                // plays in flight
const owners = new WeakMap();    // element → AbortController for its borrower's listeners

/**
 * Bless elements until the pool is full. Must run INSIDE a user gesture: the
 * play() calls are made synchronously, before anything is awaited.
 */
export function blessAudioPool() {
  if (typeof Audio === 'undefined') return;
  while (free.length + blessing < SIZE) {
    const el = new Audio();
    el.preload = 'auto';
    el.src = SILENT_WAV;
    blessing++;
    let p = null;
    try { p = el.play(); } catch { p = null; }
    const done = (ok) => {
      blessing--;
      try { el.pause(); } catch { /* ignore */ }
      if (ok) { blessed.add(el); free.push(el); }
    };
    /* ⚠ WHAT BLESSES IS THE CALL, NOT THE PLAYBACK (2026-10-30). WebKit unlocks an
       element when play() is invoked during a user gesture, whether or not that
       particular clip then plays. So a rejection still counts, unless it is
       NotAllowedError (not a gesture after all); and a play that never settles
       (seen on iOS) must not hold `blessing` up forever, which stopped the pool
       ever topping up again. */
    if (p && typeof p.then === 'function') {
      let settled = false;
      const settle = (ok) => { if (settled) return; settled = true; done(ok); };
      p.then(() => settle(true), (err) => settle(err?.name !== 'NotAllowedError'));
      setTimeout(() => settle(true), 1500);
    } else done(true);
  }
}

/**
 * An element for `url`, blessed when the pool has one (a plain new one when it
 * is empty — that still plays everywhere but iOS, exactly as before).
 */
export const poolStats = { lent: 0, plain: 0 };   // `plain`: handed out unblessed (silent on iOS) — audio debug
export function takeAudio(url) {
  let el = free.pop();
  if (!el) { el = new Audio(); poolStats.plain += 1; if (import.meta.env.DEV) console.warn('[audio] pool empty: an unblessed player (silent on iOS)'); }
  poolStats.lent += 1;
  owners.set(el, new AbortController());
  el.preload = 'auto';
  el.loop = false;
  el.muted = false;
  try { el.volume = 1; } catch { /* read-only on iOS */ }
  el.src = url;
  return el;
}

/** addEventListener, removed again when the element is handed back. */
export function onAudio(el, type, fn, opts = {}) {
  const ctrl = owners.get(el);
  el.addEventListener(type, fn, ctrl ? { ...opts, signal: ctrl.signal } : opts);
}

/** Stop it, drop its listeners and its download, and keep it for the next borrower. */
export function releaseAudio(el) {
  if (!el) return;
  poolStats.lent = Math.max(0, poolStats.lent - 1);
  try { owners.get(el)?.abort(); } catch { /* ignore */ }
  owners.delete(el);
  try { el.pause(); } catch { /* ignore */ }
  // Back to the silent clip: stops the song downloading, and stays blessed.
  try { el.src = SILENT_WAV; } catch { /* ignore */ }
  // Only a blessed one goes back: a plain one made while the pool was empty
  // would pass for blessed and fail the next borrower on iOS.
  if (blessed.has(el) && free.length < SIZE * 2) free.push(el);
}

// Every tap tops the pool up (a no-op once it is full). The activation events a
// browser counts as a user gesture — on iOS a touch ends it, not starts it.
if (typeof window !== 'undefined') {
  for (const type of ['touchend', 'pointerup', 'click', 'keydown']) {
    window.addEventListener(type, blessAudioPool, { capture: true, passive: true });
  }
}

/** How many blessed players are idle right now (audio debug). */
export function poolFree() { return free.length; }
